import React from 'react';
import { useLazyQuery } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { QUERY } from '../gql-operations';
import useAddFoldersToTree from './useAddFoldersToTree';

import { AuthContext } from '../util/context';
import { buildThreadVisibilityFilterInput } from '../util/threadVisibilityFilters';

import useUpdateGetFolderContent from '../hooks/useUpdateGetFolderContent';
import useNotificationSystem from '../components/Notifications/useNotificationSystem';

function useGetSubfolders() {
    const {
        state: { treeData, showDeletedMessages, getSubfolders, folderThreadVisibilityFilter },
        context: { setGetSubfolders }
    } = React.useContext(AuthContext);

    const { updateNewFolders } = useUpdateGetFolderContent();
    const client = useApolloClient();
    const { showCreatedFolderNotification } = useNotificationSystem();

    const getFolderContentVariableCandidates = parentId => {
        const baseData = {
            folderId: parentId,
            isTrash: showDeletedMessages,
            ...buildThreadVisibilityFilterInput(folderThreadVisibilityFilter)
        };

        return [
            { data: baseData },
            { data: { ...baseData, respectManualOrder: false } },
            { data: { ...baseData, respectManualOrder: true } }
        ];
    };

    const [addChildrenToFolder] = useAddFoldersToTree();
    const [getContent] = useLazyQuery(QUERY.GET_FOLDER_CONTENT, {
        fetchPolicy: 'cache-first',
        onCompleted: async res => {
            if (res.getFolderContent.folders.length > 0) {
                let sortedFolders = res.getFolderContent.folders;
                addChildrenToFolder(sortedFolders);
                const loading = { ...getSubfolders };
                delete loading[sortedFolders[0].parent];
            }
            setGetSubfolders({});
        }
    });

    const setChildrenManually = async parentId => {
        // Do not attempt to fetch subfolders for special non-ObjectId folders
        if (!parentId || parentId === 'X_BOOKMARKS') {
            setGetSubfolders({});
            return true;
        }
        let loading = { ...getSubfolders };
        loading[parentId] = { loading: true };
        setGetSubfolders(loading);
        getContent({
            variables: getFolderContentVariableCandidates(parentId)[0]
        });

        // because the response can not be given from lazyQuery
        return true;
    };

    const storeOrFetch = parentId => {
        try {
            if (parentId) {
                const variableCandidates = getFolderContentVariableCandidates(parentId);

                for (const variables of variableCandidates) {
                    const store = client.readQuery({
                        query: QUERY.GET_FOLDER_CONTENT,
                        variables,
                        returnPartialData: true
                    });

                    if (store?.getFolderContent?.folders?.length > 0) {
                        addChildrenToFolder(store.getFolderContent.folders);
                        return;
                    }
                }

                setChildrenManually(parentId);
            } else {
                // console.log('folder not loaded yet');
            }
        } catch (error) {
            console.log('folder not loaded yet');
            setChildrenManually(parentId);
        }
    };

    const runThis = async (folder, justCreated) => {
        if (justCreated) {
            console.log('this is the folder', folder);
            showCreatedFolderNotification(folder);
            // so we don't fetch the getContent
            if (folder.parent) {
                // this is so we reload the cache with new folder
                updateNewFolders({ id: folder.parent }, [folder]);
                let parentFolder = treeData.items[folder.parent];
                if (parentFolder && parentFolder.children.length >= 0) {
                    let response = await addChildrenToFolder([folder]);
                    console.log('just created and has a parent');
                    return response;
                } else {
                    return true;
                }
            } else {
                let response = await addChildrenToFolder([folder]);
                console.log('just created, just add him to the tree', response);
                return response;
            }
        }
        //  else if (folder.parent) {
        //     let response = await setChildrenManually(folder.parent);
        //     // RETURN is just so we can focus on the folder later on
        //     return response;
        // }
        else {
            // he's the parent, just call for him
            let response = await storeOrFetch(folder.id);
            return response;
        }
    };

    return [runThis];
}

export default useGetSubfolders;
